<script>
import { BModal } from 'bootstrap-vue';
import dayjs from '../../core/dayjs';

export default {
  components: {
    BModal,
  },

  props: {
    leadHashId: {
      type: String,
      required: true,
    }
  },

  data () {
    return {
      files: [],
      currentPage: 1,
      lastPage: 1,
      allowedDocumentTypes: ['mp4', 'webm', 'ogg', 'jpg', 'webp', 'jpeg', 'png'],
      inParams: '',
      isLoading: true,
      documentType: '',
      wasSyngooDocsLoaded: false
    };
  },

  async mounted () {
    Bus.$on('openMediaModal', (documentType) => {
      if(documentType !== 'syngoo' && this.wasSyngooDocsLoaded) {
        this.setFiles([]);
        this.wasSyngooDocsLoaded = false;
      } 
      this.documentType = documentType;
      this.openModal();
    });
  },

  destroyed () {
    Bus.$off('openMediaModal');

    this.setCurrentPage(1);
    this.setLastPage(1);
    this.setFiles([]);
  },

  methods: {
    openModal () {
      this.$refs['lead-media-modal'].show();
    },

    hideModal () {
      this.$refs['lead-media-modal'].hide();
    },

    onModalHidden() {
      if(this.documentType !== 'syngoo') {
        this.setCurrentPage(1);
        this.setLastPage(1);
        this.setFiles([]);
      }
      this.isLoading = true;
    },

    openFileModal (file) {
      if (!file) {
        return;
      }

      Bus.$emit('openFileModal', file);
    },

    completeFileName (file) {
      return `${file.title}.${file.extension}`;
    },

    formatDate (date) {
      if (!date) {
        return 'Data indefinida';
      }

      try {
        return dayjs(date).format('DD/MM/YYYY');
      } catch (error) {
        return 'Data inválida';
      }
    },

    setCurrentPage(currentPage) {
      this.currentPage = currentPage;
    },

    setLastPage(lastPage) {
      this.lastPage = lastPage;
    },

    setTypeInParam() {
      this.allowedDocumentTypes.forEach((allowedDocumentType) => {
        this.inParams = this.inParams + "&type[in][]=" + allowedDocumentType
      });
    },

    setFiles(files) {
      this.files = files;
    },

    setIsLoading(isLoading) {
      this.isLoading = isLoading;
    },

    async getImagesAndVideos() {
        try {
          return await this.$http.get(`/lead/documents/${this.leadHashId}?page=${this.currentPage}${this.inParams}&document_type=${this.documentType}`);
        } catch (error) {
          console.error('Não foi possível capturar as imagens e vídeos: ', error);
        }
    },

    async setDataWhenModalIsOpened() {
      this.setTypeInParam();

      if(this.documentType !== 'syngoo' || !this.wasSyngooDocsLoaded) {
        const { data } = await this.getImagesAndVideos();
        this.setLastPage(data.last_page);
        this.setFiles(data.data);  
        if(this.documentType === 'syngoo') this.wasSyngooDocsLoaded = true;
      }
      this.setIsLoading(false);
    },

    async fetchMore() {
      this.setIsLoading(true);

      this.setCurrentPage(this.currentPage + 1);
      const { data } = await this.getImagesAndVideos();
      this.files.push(...data.data);

      this.setIsLoading(false);
    }
  },
};
</script>

<template>
  <BModal
    id="lead-media-modal"
    ref="lead-media-modal"
    hide-footer="hide-footer"
    centered
    @shown="setDataWhenModalIsOpened()"
    @hidden="onModalHidden()"
  >
    <template slot="modal-header">
      <h2 class="modal-title">
        {{ __('Galeria').toUpperCase() }}
      </h2>

      <button
      class="close"
      type="button"
      data-dismiss="modal"
      aria-label="Close"
      @click="hideModal()"
    >
      <span aria-hidden="true" ><img
        class="mb-1"
        src="/build/icons/close@white.svg"
        alt="Fechar"
        width="14"
        height="13"
      ></span>
    </button>
    </template>
    <template slot="default">
      <a
        v-for="file in files"
        @click="openFileModal(file)"
        class="file"
      >
        <img
          v-if="file.extension !== 'mp4' && file.extension !== 'ogg'"
          class="w-100 h-100"
          :src="file.signedUrl"
          @load="file.isLoading = false"
          v-show="!file.isLoading"
        />
        <img 
          v-else-if="file.extension === 'ogg'"
          src="/build/icons/play-button.svg"
          @load="file.isLoading = false"
          v-show="!file.isLoading"
        />
        <div
          v-else
          v-show="!file.isLoading"
          class="video-container w-100 h-100"
        >
          <video
            class="w-100 h-100"
            :src="file.signedUrl"
            v-on:canplay="file.isLoading = false"
          />
          <i v-if="!file.isLoading" class="fa fa-play"/>
        </div>
        <div
          v-if="file.isLoading"
          class="w-100 h-100 d-flex align-items-center justify-content-center"
        >
          <i class="fa fa-circle-o-notch fa-spin" />
        </div>
      </a>
      <div v-if="files.length === 0 && !isLoading" class="w-100 no-data">
        <h3 class="mt-4">
          {{ __('Não há dados a serem exibidos') }}
        </h3>
      </div>
      <div v-if="files.length > 0 && lastPage > currentPage" class="w-100 d-flex align-items-center justify-content-center">
        <button v-if="!isLoading" class="load-more" @click="fetchMore()">
          {{ __('Ver mais') }}
        </button>
        <i v-else class="fa fa-circle-o-notch fa-spin" />
      </div>
      <div v-if="isLoading && files.length === 0" class="w-100 h-100 d-flex align-items-center justify-content-center">
        <i class="fa fa-circle-o-notch fa-spin" />
      </div>
    </template>
  </BModal>
</template>

<style lang="scss">
  @import '../../../sass/variables';
  @import '../../../sass/mixins';

  #lead-media-modal {
    .modal-dialog {
      min-width: 546px;
    }

    .modal-content {
      border-radius: 16px;
      overflow: hidden;
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 24px;

      .modal-title {
        @include salesflow-text($font-weight: 600, $color: white);
      }
    }

    .modal-body {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      max-height: 23.5rem;
      min-height: 23.5rem;
      padding: 16px 24px;
      overflow-y: scroll;
      background-color: $salesflow-bg-content;

      &::-webkit-scrollbar {
        display: none;
      }

      .file {
        width: 154px;
        height: 142px;
        border-radius: 8px;
        overflow: hidden;
        background: #DEE4E9;
        opacity: 0.8;
        display: flex;
        justify-content: center;
        align-items: center;

        img, video {
          object-fit: cover;
        }
      }

      .file:hover {
        opacity: 1;
        cursor: pointer;
      }

      .video-container {
        position: relative;

        i {
          position: absolute;
          z-index: 10;
          top: 50%;
          left: 50%;
          transform: translate(-25%, -50%);
          color: $salesflow-font-dark;
          font-size: 1.5rem;
          opacity: 0.8;

          &:hover {
            cursor: pointer;
          }
        }
      }

      .no-data {
        h3 {
          text-align: center;
          @include salesflow-subtitle($font-weight: 500, $color: $salesflow-font-gray);
        }
      }

      .load-more {
        padding: 0px;
        border: 0px;
        outline: 0px;
        background-color: transparent;

        @include salesflow-text;

        &:hover {
          color: $salesflow-primary-hover;
        }
      }
    }
  }
</style>
